import { useMemo, useContext, useCallback } from "react";

import { i18n } from "../../../../translate/i18n";

import { TenantContext } from "../../../../templates/context/TenantContext";

import { 
  currencyFormatter, 
  createIntFormatter, 
  createPercentFormatter 
} from "../../../../utils/formatter/formatter";
import { getMonthAbbreviation, toNDecimalsString } from "../../../../utils/utils";

import { Card, Text } from "@aidron/aidron-ds";

import {
  ButtonWithModal,
  TextAndLabelView,
  IconButtonWithModal,
  ProductAnalysisChart,
  PredictionChart,
  Carousel,
  OrderDetailsTable,  
} from "../../sharedComponents.index";
import { LOCATION_VALUES_FOR_PREDICTION_CHART } from "../../../components.constants";

import { CustomHeading } from "../../../StyledComponents/styleComponents.index";

const { t } = i18n;

const MONTH_MAP = {
  1: "Janeiro",
  2: "Fevereiro",
  3: "Março",
  4: "Abril",
  5: "Maio",
  6: "Junho",
  7: "Julho",
  8: "Agosto",
  9: "Setembro",
  10: "Outubro",
  11: "Novembro",
  12: "Dezembro"
};

const ACCORDION_TABLE_META = {
  enableSorting: true,
  sortingMode: { id: 'value', desc: true }
};

const ACCORDION_TABLE_UI = {
  hasWhiteBackground: true,
  headerStyle: { fontSize: '0.9rem', textAlign: 'left' }
};

const CELL_STYLE = { display: 'flex', justifyContent: 'start', paddingInlineStart: '4%', fontWeight: '500', color: '#374151' };

const getColumns = (unit) => [
  {
    header: t("month"),
    accessorKey: 'month',
    id: 'month',
    cell: info => <div style={CELL_STYLE}>{MONTH_MAP[Number(info.getValue())]}</div>,
    enableSorting: true
  },
  {
    header: t("currentPlan"),
    accessorKey: 'clientPrediction',
    id: 'clientPrediction',
    cell: info => (
      <div style={CELL_STYLE}>
        {unit === 'items' ? createIntFormatter().format(info.getValue()) : currencyFormatter.format(info.getValue())}
      </div>
    ),
    enableSorting: true
  },
  {
    header: t("futurePlan"),
    accessorKey: 'aidronPrediction',
    id: 'aidronPrediction',
    cell: info => (
      <div style={CELL_STYLE}>
        {unit === 'items' ? createIntFormatter().format(info.getValue()) : currencyFormatter.format(info.getValue())}
      </div>
    ),
    enableSorting: true
  },
  {
    header: t("difference"),
    accessorKey: 'differenceAidronAndClient',
    id: 'differenceAidronAndClient',
    cell: info => (
      <div style={CELL_STYLE}>
        {unit === 'items' ? createIntFormatter().format(info.getValue()) : currencyFormatter.format(info.getValue())}
      </div>
    ),
    enableSorting: true
  },
  {
    header: t("percentDiff"),
    accessorKey: 'percentAidronVsClient',
    id: 'percentAidronVsClient',
    cell: info => <div style={CELL_STYLE}>{createPercentFormatter().format(info.getValue() / 100)}</div>,
    enableSorting: true
  },
];

const getDateAbbreviation = (date) => {
  const monthAbbreviation = getMonthAbbreviation(date.getMonth() + 1);
  const yearSuffix = date.getFullYear().toString().slice(2);
  return `${monthAbbreviation}/${yearSuffix}`;
};

const adjustForTimezone = (date) => {
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() + timezoneOffset);
};

const getTextInLanguage = (textPt, textEn) => {
  const language = localStorage.getItem("i18nextLng").slice(0, 2);
  switch (language) {
    case "pt": return textPt;
    case "en": return textEn;
    default: return textPt;
  }
};

const dateToDisplay = (startDate, endDate) => {
  const start = adjustForTimezone(new Date(startDate));
  const end = adjustForTimezone(new Date(endDate));
  const startAbbreviation = getDateAbbreviation(start);
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return startAbbreviation;
  }
  return `${startAbbreviation} a ${getDateAbbreviation(end)}`;
};

const InformationsSection = ({ ctx }) => {

  const { opportunity, opportunities, getOpportunityDetails, products } = ctx || {};
  const { opportunityId, productId, value, percent, textPt, textEn, type1, startDate, endDate } = opportunity;

  const textInSelectedLanguage = getTextInLanguage(textPt, textEn);

  const opportunityDetails = getOpportunityDetails(type1);
  const opportunityTitle = opportunityDetails.title;
  const percentLabel = getOpportunityDetails(type1).title;

  const otherOpportunities = opportunities?.filter(
    opportunity => opportunity.productId === productId && opportunity.opportunityId !== opportunityId
  );

  const otherOpportunitiesText = otherOpportunities?.length === 1
    ? t("productHasOtherOpportunity")
    : t("productHasOtherOpportunities");

  const renderAdvancedAnalysis = useCallback(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <ProductAnalysisChart ctx={{ products: products, productId: productId }} />
      </div>
    ),
    [productId, products]
  );

  const renderCarouselModal = useCallback(
    () => (
      <Carousel slideNumber={otherOpportunities.length}>
        {otherOpportunities.map((opportunity, index) => (
          <div
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
            className={`carousel-item ${index === 0 ? "active" : ""}`}
            key={index}
          >
            <div style={{ inlineSize: '90%', display: 'flex', flexDirection: 'column' }}>
              <OpportunityAccordion
                ctx={{
                  products: products,
                  opportunity: opportunity,
                  getOpportunityDetails: getOpportunityDetails,
                }}
              />
            </div>
          </div>
        ))}
      </Carousel>
    ),
    [otherOpportunities, getOpportunityDetails, products]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', blockSize: '100%', margin: '1rem' }}>
      <header style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
        <CustomHeading size="sm" margin="0">
          {t("opportunityTypeTitle", { opportunityTitle })}
        </CustomHeading>

        <IconButtonWithModal
          icon="help-circle"
          modalHeading={`${t("help")}: ${t("opportunityTypeTitle", { opportunityTitle })}`}
          renderModalContent={() => <Text>{opportunityDetails.summary}</Text>}
        />
      </header>

      <div style={{ display: 'flex', justifyContent: 'space-around', marginInlineEnd: '1.5rem' }}>
        <TextAndLabelView
          label={percentLabel}
          heading={`${percent > 0 ? "↑" : "↓"} ${toNDecimalsString(Math.abs(percent), 0)}%`}
        />
        <TextAndLabelView label={t("opportunity")} heading={`R$ ${toNDecimalsString(value, 0)}`} textSize="lg" />
        <TextAndLabelView label={t("period")} heading={dateToDisplay(startDate, endDate)} textSize="lg" />
      </div>

      <hgroup>
        <CustomHeading fontSize={['0.4rem', '0.8rem', '1.2rem']} margin="0">
          {t("actionRecommendation")}:
        </CustomHeading>

        <p>{textInSelectedLanguage.replace(/([.!?])\s/g, "$1\n")}</p>
      </hgroup>

      <div style={{ display: 'flex', justifyContent: 'center', marginBlockEnd: '0.5rem' }}>
        <ButtonWithModal
          ctx={{
            label: t("advancedAnalysis"),
            type: "filled",
            renderModalContent: renderAdvancedAnalysis
          }}
        />
      </div>

      {opportunities && otherOpportunities.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <p style={{ fontSize: '0.8rem' }}>{otherOpportunitiesText}</p>
            <ButtonWithModal
              ctx={{
                label: otherOpportunities?.length === 1 ? "Analisar Oportunidade" : "Analisar Oportunidades",
                modalWidth: "90%",
                renderModalContent: renderCarouselModal    
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const OpportunityAccordion = ({ ctx, uiState }) => {

  const { unit } = useContext(TenantContext);

  const { 
    opportunity, 
    opportunities, 
    getOpportunityDetails, 
    predictions = [], 
    products = [] 
  } = ctx || {};

  const { accordionMode } = uiState || {};

  const { productId, forecastId } = opportunity;

  const predictionsByLatestForecastId = predictions.filter(
    prediction => prediction.forecastId === forecastId && prediction.productId === productId
  );

  const columns = useMemo(() => getColumns(unit), [unit]);

  const renderAdvancedAnalysis = useCallback(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <ProductAnalysisChart ctx={{ products: products, productId: productId }} />
      </div>
    ),
    [productId, products]
  );

  if (accordionMode === 'advancedOpportunities') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Card style={{ width: "80%", height: "100%" }}>
            <PredictionChart ctx={{ productId: productId }} />
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', blockSize: '100%', margin: '1rem' }}>
          <OrderDetailsTable 
            ctx={{
              tableSourceData: predictionsByLatestForecastId || [],
              columns: columns || [],
              tableSourceMetaData: ACCORDION_TABLE_META
            }}
            uiState={ACCORDION_TABLE_UI}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBlockEnd: '0.5rem' }}>
          <ButtonWithModal
            ctx={{
              label: t("advancedAnalysis"),
              type: "filled",
              renderModalContent: renderAdvancedAnalysis
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
      <InformationsSection
        ctx={{
          opportunity: opportunity,
          opportunities: opportunities,
          products: products,
          getOpportunityDetails: getOpportunityDetails
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Card style={{ width: "80%", height: "100%" }}>
          <PredictionChart 
            ctx={{ productId: productId }} 
            uiState={{
              location: LOCATION_VALUES_FOR_PREDICTION_CHART.OPPORTUNITY_ACCORDION
            }}
          />
        </Card>
      </div>
    </div>
  );
};